/**
 * SharedMapPage — `/share/:token/:singular?/:itemId?`
 *
 * Full read-only shared map: left list panel (desktop inline / mobile
 * bottom-sheet) + the public map shell (full authenticated-map parity:
 * SDF markers, clustering, toolbar, layers panel, 2D/3D) + a right
 * read-only detail panel driven by the URL. Shell-less (no AppShell, no
 * ProtectedRoute). Password gate shown when the share requires one.
 *
 * @module pages/SharedMapPage
 */
import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Portal } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { FloatingPanel } from '@/components/ui/FloatingPanel';
import { SidePanel } from '@/components/ui/SidePanel';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { useHideMobileChrome } from '@/hooks/useHideMobileChrome';
import {
  PublicArtifactDetail,
  PublicArtifactList,
  PublicControls,
  PublicFloatingControls,
  PublicListToggle,
  PublicMapShell,
  PublicShareGate,
  usePublicSharePage,
} from '@/components/share/public';

import styles from './SharedMapPage.module.css';

export function SharedMapPage(): ReactElement {
  const { token = '' } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();
  // Hide the floating controls + mobile list toggle when the detail panel covers the map on mobile.
  const showFloatingUI = !useHideMobileChrome();

  const [mobileListOpen, setMobileListOpen] = useState(false);
  const closeMobileList = useCallback(() => setMobileListOpen(false), []);
  const toggleMobileList = useCallback(
    () => setMobileListOpen((open) => !open),
    []
  );

  const [listCollapsed, setListCollapsed] = useState(false);
  const collapseList = useCallback(() => setListCollapsed(true), []);
  const expandList = useCallback(() => setListCollapsed(false), []);

  // Folded into the detail panel toolbar on desktop (the panel covers the
  // floating pill in fullscreen). Memoized so the stable element doesn't defeat
  // PublicArtifactDetail's React.memo on every list-toggle re-render.
  const detailToolbarActions = useMemo(
    () => (!isMobile ? <PublicControls /> : undefined),
    [isMobile]
  );

  const {
    status,
    passwordError,
    rateLimited,
    config,
    listData,
    mapData,
    descriptor,
    artifact,
    isDetailLoading,
    detailError,
    viewerFilters,
    setViewerFilters,
    handleSelect,
    handleSubmitPassword,
    closeOverlay,
  } = usePublicSharePage(token, { onAfterSelect: closeMobileList });

  if (status !== 'ready' || !config || !listData || !mapData) {
    return (
      <PublicShareGate
        status={status}
        passwordError={passwordError}
        rateLimited={rateLimited}
        onSubmitPassword={handleSubmitPassword}
      />
    );
  }

  // One list instance; only one branch (desktop aside / mobile sheet) renders
  // at a time, so the collapse handler can switch on `isMobile`.
  const listElement = (
    <PublicArtifactList
      config={config}
      listData={listData}
      viewerFilters={viewerFilters}
      onViewerFiltersChange={setViewerFilters}
      onSelect={handleSelect}
      onCollapse={isMobile ? closeMobileList : collapseList}
      listGutter="sm"
    />
  );

  return (
    <div className={styles.page} data-chromeless>
      {!isMobile && (
        <aside
          className={`${styles.sidebar} ${listCollapsed ? styles.sidebarCollapsed : ''}`}
          aria-hidden={listCollapsed}
        >
          <div className={styles.sidebarInner}>{listElement}</div>
        </aside>
      )}

      <div className={styles.mapArea}>
        <PublicMapShell
          token={token}
          config={config}
          withAddressSearch
          centerBrandOnMobile
          searchInsetForListToggle={!isMobile && listCollapsed}
        />
        {/* Floating language/theme pill over the map. In desktop fullscreen the
            detail panel (z-fullscreen) covers it and folds these controls into
            its own toolbar; in docked/sidepanel mode it stays visible. */}
        {showFloatingUI && <PublicFloatingControls />}
        {!isMobile && listCollapsed && (
          <FloatingPanel
            position="top-left"
            aria-label={t('share.public.listToggle')}
          >
            <PublicListToggle opened={false} onToggle={expandList} />
          </FloatingPanel>
        )}
      </div>

      {isMobile && (
        <>
          {showFloatingUI && (
            <FloatingPanel
              position="top-left"
              aria-label={t('share.public.listToggle')}
            >
              <PublicListToggle
                opened={mobileListOpen}
                onToggle={toggleMobileList}
              />
            </FloatingPanel>
          )}
          {/* Portal lifts the list out of the page's fixed-position stacking
              context (which traps its z-index) so it shares the body-level
              layer with the portaled detail SlidePanel and can stack above it. */}
          <Portal>
            <SidePanel isOpen={mobileListOpen} onClose={closeMobileList}>
              <div className={styles.mobileListContent}>{listElement}</div>
            </SidePanel>
          </Portal>
        </>
      )}

      <PublicArtifactDetail
        descriptor={descriptor}
        artifact={artifact}
        isDetailLoading={isDetailLoading}
        detailError={detailError}
        config={config}
        listData={listData}
        token={token}
        toolbarActions={detailToolbarActions}
        onClose={closeOverlay}
      />
    </div>
  );
}
